import type {
  MediaBlock,
  MediaBlockValueType,
} from '@basehub/data/replicache/index'

export const VALID_IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/svg+xml',
  'image/webp',
  'image/avif',
  'image/gif',
  'image/tiff',
  'image/bmp',
  'image/x-icon',
  'image/vnd.microsoft.icon', // alternative ico MIME type
  'image/apng', // Animated PNG
  'image/heic', // iPhone/iOS photos
  'image/heif', // High Efficiency Image Format
  'image/jp2', // JPEG 2000
  'image/jxl', // JPEG XL
  'image/x-ms-bmp', // alternative BMP MIME type
]

export const SUGGESTED_ALT_TEXT_COMPATIBLE_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/avif',
  'image/gif',
]

export const OG_IMAGE_COMPATIBLE_MIME_TYPES = ['image/jpeg', 'image/png']

export const VALID_VIDEO_MIME_TYPES = [
  'video/mp4',
  'video/x-msvideo', // avi
  'video/quicktime', // mov
  'video/x-matroska', // mkv
  'video/x-flv', // flv
  'video/mpeg',
  'video/ogg', // ogv
  'video/webm',
  'video/3gpp', // 3gp
  'video/3gpp2', // 3g2
  'video/x-ms-wmv', // wmv
  'video/mp2t', // MPEG transport stream (ts)
  'video/x-f4v', // Flash Video
  'video/divx', // DivX
]

export const VALID_AUDIO_MIME_TYPES = [
  'audio/mpeg', // mp3
  'audio/aac',
  'audio/wav',
  'audio/flac',
  'audio/webm', // weba
  'audio/opus',
  'audio/ogg', // oga
  'audio/midi',
  'audio/x-ms-wma', // wma
  'audio/x-wav',
  'audio/amr',
  'audio/mp4',
  'audio/x-m4a',
  'audio/m4a',
  'audio/3gpp', // 3gp audio
  'audio/3gpp2', // 3g2 audio
  'audio/x-aiff', // AIFF
  'audio/aiff', // AIFF alternative
  'audio/x-ms-wax', // Windows Media Audio redirector
  'audio/vnd.rn-realaudio', // RealAudio
  'audio/x-pn-realaudio', // RealAudio alternative
  'audio/x-realaudio', // RealAudio alternative
]

export const DEFAULT_FILE_MIME_TYPES = [
  'application/pdf',
  'application/msword', // doc
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // docx
  'application/vnd.ms-excel', // xls
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // xlsx
  'text/csv',
  'font/otf',
  'font/ttf',
  'font/woff',
  'font/woff2',
  'application/x-font-woff',
  'application/x-font-woff2',
  'application/x-font-opentype',
  'application/x-font-ttf',
  'application/zip',
  'application/vnd.ms-powerpoint', // ppt
  'application/vnd.openxmlformats-officedocument.presentationml.presentation', // pptx
  'application/rtf',
  'application/x-tar',
  'text/plain',
  'text/html',
  'application/json',
  'application/xml',
  'application/vnd.oasis.opendocument.text', // odt
  'model/gltf-binary', // glb
  'model/gltf+json', // gltf
  'application/octet-stream', // common fallback for 3D files
  'model/obj', // obj
  'model/stl', // stl
  'model/fbx', // fbx
  'model/vnd.usdz+zip', // usdz
  'application/vnd.collada+xml', // dae
]

export const DEFAULT_MERGED_MEDIA_MIME_TYPES = [
  ...VALID_IMAGE_MIME_TYPES,
  ...VALID_VIDEO_MIME_TYPES,
  ...VALID_AUDIO_MIME_TYPES,
  ...DEFAULT_FILE_MIME_TYPES,
]

export const isValidImageType = (type: string) =>
  VALID_IMAGE_MIME_TYPES.includes(type)

export const isSuggestedAltTextCompatibleType = (type: string) =>
  SUGGESTED_ALT_TEXT_COMPATIBLE_MIME_TYPES.includes(type)

export const isOgImageCompatibleType = (type: string) =>
  OG_IMAGE_COMPATIBLE_MIME_TYPES.includes(type)

export const isValidVideoType = (type: string) =>
  VALID_VIDEO_MIME_TYPES.includes(type)

export const isValidAudioType = (type: string) =>
  VALID_AUDIO_MIME_TYPES.includes(type)

export const isValidTypeForCategory = (
  type: string,
  categories: MediaBlockValueType[]
) => {
  let isValid = false
  if (categories.includes('file')) return true
  if (categories.includes('image')) {
    isValid = isValidImageType(type)
  }
  if (categories.includes('video') && !isValid) {
    isValid = isValidVideoType(type)
  }
  if (categories.includes('audio') && !isValid) {
    isValid = isValidAudioType(type)
  }
  return isValid
}

export function getDefaultMimeTypesByCategory(category: string) {
  switch (category) {
    case 'image':
      return VALID_IMAGE_MIME_TYPES
    case 'video':
      return VALID_VIDEO_MIME_TYPES
    case 'audio':
      return VALID_AUDIO_MIME_TYPES
    case 'file':
      return DEFAULT_FILE_MIME_TYPES
    default:
      return []
  }
}

export function getAllowedMimeTypesArray(
  allowedMimeTypes: MediaBlock['constraints']['allowedMimeTypes']
): string[] {
  return Object.entries(allowedMimeTypes ?? {}).reduce<string[]>(
    (acc, [cat, mimeTypes]) => {
      if (!mimeTypes || (Array.isArray(mimeTypes) && mimeTypes.length === 0)) {
        return acc
      }
      if (Array.isArray(mimeTypes)) {
        acc.push(...mimeTypes)
      } else {
        acc.push(...getDefaultMimeTypesByCategory(cat))
      }
      return acc
    },
    []
  )
}

export function allowsAnyFile(
  allowedMimeTypes: MediaBlock['constraints']['allowedMimeTypes']
) {
  return allowedMimeTypes?.['file'] === true
}

export function getMediaTypeByMimeType(
  mimeType: File['type']
): MediaBlockValueType {
  if (VALID_IMAGE_MIME_TYPES.includes(mimeType)) {
    return 'image'
  }
  if (VALID_VIDEO_MIME_TYPES.includes(mimeType)) {
    return 'video'
  }
  if (VALID_AUDIO_MIME_TYPES.includes(mimeType)) {
    return 'audio'
  }
  return 'file'
}
